import {
  DISCARD_SCHEME_DRAFT,
  PUBLISH_SCHEME_DRAFT,
  UPDATE_SCHEME_DRAFT,
} from '@/mutations';
import { GET_ORGANISATION_DETAIL, GET_SCHEME_TREE_AND_DRAFT } from '@/queries';
// import { useDebugReducer } from './useDebugReducer';
import { OrganisationContext, UserContext } from '@/context';
import { getUniqueKey, schemeHelperFactory } from '@hogwarts/scheme-profiles';
import {
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useRef,
  useState,
} from 'react';
import { initialState, reducer } from './reducer';

import { useMutation } from '@/hooks';
import { useQuery } from '@apollo/client';
import { isEqual } from 'lodash';

const SAVE_DEBOUNCE_TIME = 950;

const isEmptyScheme = (data) => {
  // eslint-disable-next-line no-unused-vars
  const { version, ...items } = data || {};

  let count = 0;
  for (const itemKey of Object.keys(items || {})) {
    count += Object.keys(items[itemKey] || {}).length;
  }

  return count === 0;
};

const getActions = (dispatch, state) => {
  return {
    addField(section, dataType, inputType, inputMeta, label, appearAfterKey) {
      const key = getUniqueKey();
      dispatch({
        type: 'FIELD_ADD',
        key,
        section,
        dataType,
        inputType,
        inputMeta,
        label,
        appearAfterKey,
      });
      return key;
    },
    addArrayField(
      parentKey,
      dataType,
      inputType,
      inputMeta,
      label,
      appearAfterKey
    ) {
      const key = getUniqueKey();
      dispatch({
        type: 'ARRAYFIELD_ADD',
        key,
        parentKey,
        dataType,
        inputType,
        inputMeta,
        label,
        appearAfterKey,
      });
      return key;
    },

    addSection(label) {
      const order =
        Math.max(...state.scheme.sections.map((s) => s.order || 0)) + 10;
      const key = getUniqueKey();
      dispatch({
        type: 'SECTION_ADD',
        key,
        label,
        order,
      });
      return key;
    },

    sectionUpdated(updated) {
      if (Object.keys(updated).length === 0) return;
      dispatch({
        type: 'SECTION_UPDATED',
        updated,
      });
    },

    fieldUpdated(updated) {
      if (Object.keys(updated).length === 0) return;
      dispatch({
        type: 'FIELD_UPDATED',
        updated,
      });
    },

    sectionReorder(movedKey, bumpedKey) {
      dispatch({
        type: 'SECTION_REORDER',
        movedKey,
        bumpedKey,
      });
    },

    profileTypeReorder(movedKey, bumpedKey) {
      dispatch({
        type: 'PROFILE_TYPE_REORDER',
        movedKey,
        bumpedKey,
      });
    },

    fieldReorder(section, movedKey, bumpedKey) {
      dispatch({
        type: 'FIELD_REORDER',
        section,
        movedKey,
        bumpedKey,
      });
    },

    arrayFieldReorder(section, fieldKey, movedKey, bumpedKey) {
      dispatch({
        type: 'ARRAYFIELD_REORDER',
        section,
        fieldKey,
        movedKey,
        bumpedKey,
      });
    },

    sectionDelete(section) {
      dispatch({
        type: 'SECTION_DELETE',
        key: section,
      });
    },

    sectionRestore(section) {
      dispatch({
        type: 'SECTION_RESTORE',
        key: section,
      });
    },

    fieldDelete(field) {
      dispatch({
        type: 'FIELD_DELETE',
        key: field,
      });
    },

    fieldRestore(field) {
      dispatch({
        type: 'FIELD_RESTORE',
        key: field,
      });
    },

    profileTypeUpdated(updated) {
      if (Object.keys(updated).length === 0) return;
      dispatch({ type: 'PROFILE_TYPE_UPDATED', updated });
    },

    addProfileType({ parent, label, description, avatar, enabled, color }) {
      if (!parent) return null;
      const key = getUniqueKey();
      dispatch({
        type: 'PROFILE_TYPE_ADD',
        key,
        parent,
        label,
        description,
        avatar,
        enabled,
        color,
      });
      return key;
    },

    profileTypeDelete(key) {
      dispatch({ type: 'PROFILE_TYPE_DELETE', key });
    },

    profileTypeRestore(key) {
      dispatch({ type: 'PROFILE_TYPE_RESTORE', key });
    },

    ratingSystemReorder(movedKey, bumpedKey) {
      dispatch({
        type: 'RATING_SYSTEM_REORDER',
        movedKey,
        bumpedKey,
      });
    },

    ratingSystemUpdated(updated) {
      if (Object.keys(updated).length === 0) return;
      dispatch({ type: 'RATING_SYSTEM_UPDATED', updated });
    },
  };
};

export const useSchemeLoader = (schemeId, profileTypeKey) => {
  const user = useContext(UserContext);
  const organisation = useContext(OrganisationContext);

  const [state, dispatch] = useReducer(
    reducer(user.id, profileTypeKey),
    initialState
  );
  const stateRef = useRef(state);
  stateRef.current = state;

  const [savePending, setSavePending] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const debounceTimerRef = useRef(null);
  const saveInFlightRef = useRef(null);
  const saveDirtyRef = useRef(false);
  const skipAutosaveRef = useRef(false);
  const publishingRef = useRef(false);
  const flushingRef = useRef(false);
  const lastSavedChangeSetRef = useRef(null);

  const refreshSchemeTreeAndDraft = useCallback(
    ({ schemeTree, schemeDraft }) => {
      const scheme = schemeHelperFactory(schemeTree, {
        includeDeleted: true,
        calculateUsage: true,
      });

      const leafScheme = schemeTree[schemeTree.length - 1];
      const schemeName = leafScheme.name;
      const schemeOptions = leafScheme.options;
      const changeSet = schemeDraft?.data || {};

      // Treat server-loaded draft as already persisted so tab switches don't re-save
      lastSavedChangeSetRef.current = changeSet;

      dispatch({
        type: 'SCHEME_SETUP',
        scheme,
        schemeName,
        schemeOptions,
        hasChanges: !!schemeDraft?.id,
        changeSet,
      });
    },
    [dispatch]
  );

  const {
    loading,
    error,
    refetch: refreshScheme,
  } = useQuery(GET_SCHEME_TREE_AND_DRAFT, {
    fetchPolicy: 'network-only',
    variables: {
      id: schemeId,
    },
    onCompleted: refreshSchemeTreeAndDraft,
  });

  const [saveDraftScheme, { loading: saving }] = useMutation(
    UPDATE_SCHEME_DRAFT,
    {
      variables: {
        id: schemeId,
      },
    }
  );

  const [discardDraftScheme] = useMutation(DISCARD_SCHEME_DRAFT, {
    variables: {
      id: schemeId,
    },
  });

  const [publishDraftScheme] = useMutation(PUBLISH_SCHEME_DRAFT, {
    variables: {
      id: schemeId,
    },
    refetchQueries: [
      {
        query: GET_ORGANISATION_DETAIL,
        variables: {
          organisationKey: organisation?.key,
        },
      },
    ],
  });

  const saveDraftSchemeRef = useRef(saveDraftScheme);
  saveDraftSchemeRef.current = saveDraftScheme;

  const persistLatestDraft = useCallback(async () => {
    // Block autosaves that race with publish/discard after the draft is gone.
    // Flush is explicitly allowed via flushingRef.
    if (publishingRef.current && !flushingRef.current) {
      return;
    }

    if (saveInFlightRef.current) {
      saveDirtyRef.current = true;
      return saveInFlightRef.current;
    }

    const runSave = async () => {
      do {
        saveDirtyRef.current = false;
        if (publishingRef.current && !flushingRef.current) {
          break;
        }
        const { changeSet, hasChanges } = stateRef.current;
        const shouldSave = !isEmptyScheme(changeSet) || hasChanges;
        if (shouldSave) {
          await saveDraftSchemeRef.current({
            variables: {
              data: changeSet,
            },
          });
          lastSavedChangeSetRef.current = changeSet;
        }
      } while (saveDirtyRef.current);
    };

    saveInFlightRef.current = runSave().finally(() => {
      saveInFlightRef.current = null;
    });

    return saveInFlightRef.current;
  }, []);

  const clearDebounceTimer = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, []);

  const flushSave = useCallback(async () => {
    clearDebounceTimer();
    setSavePending(true);
    flushingRef.current = true;
    try {
      await persistLatestDraft();
    } finally {
      flushingRef.current = false;
      if (!saveInFlightRef.current) {
        setSavePending(false);
      }
    }
  }, [clearDebounceTimer, persistLatestDraft]);

  const cancelPendingSave = useCallback(() => {
    clearDebounceTimer();
    saveDirtyRef.current = false;
    setSavePending(false);
  }, [clearDebounceTimer]);

  const discardChanges = useCallback(async () => {
    // Block new autosaves, then wait for any save already past debounce so it
    // cannot recreate the draft after discard (update→null→create race).
    publishingRef.current = true;
    skipAutosaveRef.current = true;
    clearDebounceTimer();
    saveDirtyRef.current = false;
    setSavePending(false);
    try {
      // Wait only to avoid save-after-discard recreating the draft. Save
      // failures are irrelevant to discard — don't block or surface them.
      const inFlightSave = saveInFlightRef.current;
      if (inFlightSave) {
        await inFlightSave.catch(() => {});
      }
      // Clear again in case the in-flight loop marked dirty before seeing publishingRef
      saveDirtyRef.current = false;

      await discardDraftScheme();
      const { data } = await refreshScheme();
      refreshSchemeTreeAndDraft(data);
      dispatch({
        type: 'DISCARD_CHANGESET',
      });
    } finally {
      publishingRef.current = false;
      skipAutosaveRef.current = false;
    }
  }, [
    clearDebounceTimer,
    discardDraftScheme,
    dispatch,
    refreshScheme,
    refreshSchemeTreeAndDraft,
  ]);

  const publishChanges = useCallback(async () => {
    publishingRef.current = true;
    setPublishing(true);
    skipAutosaveRef.current = true;
    try {
      await flushSave();
      // Prevent any trailing dirty-loop / debounced save after draft is discarded
      saveDirtyRef.current = false;
      cancelPendingSave();
      const { changeSet } = stateRef.current;
      await publishDraftScheme({
        variables: {
          id: schemeId,
          data: changeSet,
        },
      });
      const { data } = await refreshScheme();
      refreshSchemeTreeAndDraft(data);
    } finally {
      publishingRef.current = false;
      skipAutosaveRef.current = false;
      setPublishing(false);
    }
  }, [
    cancelPendingSave,
    flushSave,
    publishDraftScheme,
    refreshScheme,
    refreshSchemeTreeAndDraft,
    schemeId,
  ]);

  // TODO: Put this as an Action for the Parent to call?
  useEffect(
    () => {
      if (loading || error) return;
      if (!state.initialised) return;

      dispatch({
        type: 'PROFILE_TYPE_CHANGED',
        profileTypeKey,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [profileTypeKey]
  );

  useEffect(
    () => {
      if (!state.initialised) return;
      if (!state.changeSet) return;
      if (publishingRef.current) {
        return;
      }
      if (skipAutosaveRef.current) {
        skipAutosaveRef.current = false;
        lastSavedChangeSetRef.current = state.changeSet;
        return;
      }
      // Tab switches clone changeSet without content changes — don't re-save
      if (isEqual(lastSavedChangeSetRef.current, state.changeSet)) {
        return;
      }

      setSavePending(true);
      clearDebounceTimer();
      debounceTimerRef.current = setTimeout(() => {
        debounceTimerRef.current = null;
        persistLatestDraft().finally(() => {
          if (!debounceTimerRef.current && !saveInFlightRef.current) {
            setSavePending(false);
          }
        });
      }, SAVE_DEBOUNCE_TIME);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.changeSet]
  );

  useEffect(() => () => clearDebounceTimer(), [clearDebounceTimer]);

  if (loading) {
    return { loading: true, data: null, error: null };
  } else if (error) {
    return { loading: false, data: null, error };
  }

  const busy = saving || savePending || publishing;

  return {
    ...getActions(dispatch, state),
    discardChanges,
    publishChanges,
    saving: busy,
    loading,
    initialised: state.initialised,
    scheme: state.scheme,
    schemeOptions: state.schemeOptions,
    hasChanges: state.hasChanges,
    error,
  };
};
